import { supabase } from '../lib/supabase';
import type { User } from '../types';

export const authService = {
  async signup(data: {
    rollNo: string;
    name: string;
    email: string;
    department: string;
    password: string;
    selfie?: string;
  }) {
    // Step 1: Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Failed to create user');

    // Step 2: Upload profile picture if provided
    let profilePictureUrl = null;
    if (data.selfie) {
      const base64Data = data.selfie.split(',')[1];
      const fileName = `${authData.user.id}/profile-${Date.now()}.jpg`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, Buffer.from(base64Data, 'base64'), {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
      } else {
        const { data: { publicUrl } } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(fileName);
        profilePictureUrl = publicUrl;
      }
    }

    // Step 3: Create profile record with pending status
    const profileData: any = {
      id: authData.user.id,
      roll_no: data.rollNo,
      name: data.name,
      email: data.email,
      department: data.department,
      points: 0,
      rank: 'beginner',
      streak: 0,
      login_streak: 1,
      total_activities: 0,
      before_after_count: 0,
      video_count: 0,
      last_login_date: new Date().toISOString(),
      status: 'pending', // New users need admin approval
    };
    
    // Add profile picture URL if available
    if (profilePictureUrl) {
      profileData.profile_picture = profilePictureUrl;
      profileData.avatar_url = profilePictureUrl; // Some tables might use avatar_url
    }
    
    
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single();

    if (profileError) {
      // If profile creation fails, try to delete the auth user
      // Note: This requires service role key, so we'll just throw the error
      // In production, you'd want a server-side function to clean up
      throw new Error('Failed to complete registration. Please try again.');
    }

    return {
      user: authData.user,
      profile,
    };
  },

  async login(emailOrRollNo: string, password: string) {
    let email = emailOrRollNo;
    
    // Check if it's an email or roll number
    if (!emailOrRollNo.includes('@')) {
      // It's a roll number, get the email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('roll_no', emailOrRollNo)
        .single();

      if (profileError || !profile) {
        throw new Error('Invalid email or password');
      }
      
      email = profile.email;
    }

    // Sign in with email and password
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      throw new Error('Invalid email or password');
    }

    // Check if user is approved
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('status')
      .eq('id', authData.user.id)
      .single();

    if (profileError || !profile) {
      await supabase.auth.signOut();
      throw new Error('Profile not found');
    }

    if (profile.status === 'pending') {
      await supabase.auth.signOut();
      throw new Error('Your account is pending approval. Please wait for an admin to approve your registration.');
    }

    if (profile.status === 'rejected') {
      await supabase.auth.signOut();
      throw new Error('Your account registration was rejected. Please contact support.');
    }

    // Update login streak
    await supabase.rpc('update_login_streak', { user_id: authData.user.id });

    return authData;
  },

  async resetPasswordForEmail(email: string) {
    // First check if the email exists in our system
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .single();

    if (profileError || !profile) {
      throw new Error('No account found with this email address');
    }

    // If email exists, send the reset password email
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      throw error;
    }
  },

  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      throw error;
    }
  },

  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) return null;

    // Get full profile data
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (profileError || !profile) return null;

    // Check if user is approved
    if (profile.status !== 'approved') {
      // User exists but not approved, return null
      return null;
    }

    // Get achievements
    const { data: achievements } = await supabase
      .from('achievements')
      .select('*')
      .eq('profile_id', user.id);

    // Get comment count (activity feed comments)
    const { count: commentCount } = await supabase
      .from('comments')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', user.id);

    // Get total reactions received on user's activities
    const { data: userActivities } = await supabase
      .from('activities')
      .select('id')
      .eq('profile_id', user.id);
    
    let totalReactionsReceived = 0;
    let postsWithHighReactions = 0;
    let maxReactionsOnSinglePost = 0;
    
    if (userActivities && userActivities.length > 0) {
      const activityIds = userActivities.map(a => a.id);
      const { data: reactions } = await supabase
        .from('reactions')
        .select('activity_id')
        .in('activity_id', activityIds);
      
      if (reactions && reactions.length > 0) {
        totalReactionsReceived = reactions.length;
        
        // Count reactions per post
        const reactionCounts: Record<string, number> = {};
        reactions.forEach(r => {
          reactionCounts[r.activity_id] = (reactionCounts[r.activity_id] || 0) + 1;
        });
        
        // Count posts with 20+ reactions
        postsWithHighReactions = Object.values(reactionCounts).filter(count => count >= 20).length;
        
        // Get max reactions on single post (for Influencer achievement)
        maxReactionsOnSinglePost = Math.max(...Object.values(reactionCounts));
      }
    }

    // Map achievements from database format to app format
    // DB schema: achievement_type (varchar), achievement_data (jsonb), unlocked_at
    const mappedAchievements = (achievements || []).map(a => ({
      id: a.achievement_type,
      name: a.achievement_data?.name || a.achievement_type,
      description: a.achievement_data?.description || '',
      icon: a.achievement_data?.icon || '🏆',
      category: a.achievement_data?.category || 'special',
      unlockedAt: a.unlocked_at ? new Date(a.unlocked_at) : undefined
    }));

    // Map to User type
    const userData: User = {
      id: profile.id,
      rollNo: profile.roll_no,
      name: profile.name,
      department: profile.department,
      email: profile.email,
      points: profile.points,
      level: 1, // Default level
      joinedAt: new Date(profile.created_at || Date.now()),
      rank: profile.rank as User['rank'],
      achievements: mappedAchievements,
      streak: profile.streak,
      loginStreak: profile.login_streak,
      totalActivities: profile.total_activities,
      beforeAfterCount: profile.before_after_count,
      videoCount: profile.video_count,
      lastActivityDate: profile.last_activity_date,
      lastLoginDate: profile.last_login_date,
      avatarUrl: profile.avatar_url || profile.profile_picture,
      status: profile.status,
      isAdmin: profile.is_admin || false,
      commentCount: commentCount || 0,
      totalReactionsReceived,
      postsWithHighReactions,
      maxReactionsOnSinglePost,
    };

    return userData;
  },

  async updateProfile(userId: string, updates: Partial<User>) {
    const updateData: any = {};
    
    // Map User fields to database columns
    if (updates.points !== undefined) updateData.points = updates.points;
    if (updates.rank !== undefined) updateData.rank = updates.rank;
    if (updates.streak !== undefined) updateData.streak = updates.streak;
    if (updates.loginStreak !== undefined) updateData.login_streak = updates.loginStreak;
    if (updates.totalActivities !== undefined) updateData.total_activities = updates.totalActivities;
    if (updates.beforeAfterCount !== undefined) updateData.before_after_count = updates.beforeAfterCount;
    if (updates.videoCount !== undefined) updateData.video_count = updates.videoCount;
    if (updates.lastActivityDate !== undefined) updateData.last_activity_date = updates.lastActivityDate;
    if (updates.lastLoginDate !== undefined) updateData.last_login_date = updates.lastLoginDate;
    if (updates.avatarUrl !== undefined) updateData.avatar_url = updates.avatarUrl;
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;

    // Save new achievements to database
    // DB schema: achievement_type (varchar), achievement_data (jsonb), unlocked_at
    if (updates.achievements && updates.achievements.length > 0) {
      // Get existing achievement types
      const { data: existingAchievements } = await supabase
        .from('achievements')
        .select('achievement_type')
        .eq('profile_id', userId);
      
      const existingIds = new Set((existingAchievements || []).map(a => a.achievement_type));
      
      // Filter out achievements that already exist in DB
      const newAchievements = updates.achievements.filter(a => !existingIds.has(a.id));
      
      if (newAchievements.length > 0) {
        // Insert achievements one by one with upsert to prevent duplicates
        for (const a of newAchievements) {
          const { error: insertError } = await supabase
            .from('achievements')
            .upsert({
              profile_id: userId,
              achievement_type: a.id,
              achievement_data: {
                name: a.name,
                description: a.description,
                icon: a.icon,
                category: a.category
              },
              unlocked_at: a.unlockedAt ? new Date(a.unlockedAt).toISOString() : new Date().toISOString()
            }, {
              onConflict: 'profile_id,achievement_type',
              ignoreDuplicates: true
            });
          
          if (insertError && !insertError.message.includes('duplicate')) {
            console.error('Failed to save achievement:', a.id, insertError);
          }
        }
      }
    }

    return data;
  },
};

// For client-side use without Buffer
export const authServiceClient = {
  ...authService,
  async login(emailOrRollNo: string, password: string) {
    return authService.login(emailOrRollNo, password);
  },
  async resetPasswordForEmail(email: string) {
    return authService.resetPasswordForEmail(email);
  },
  async updatePassword(newPassword: string) {
    return authService.updatePassword(newPassword);
  },
  async signup(data: {
    rollNo: string;
    name: string;
    email: string;
    department: string;
    password: string;
    selfie?: string;
  }) {
    // Step 0: Check if roll number already exists
    const { data: existingRollNo } = await supabase
      .from('profiles')
      .select('id, roll_no')
      .eq('roll_no', data.rollNo)
      .single();
    
    if (existingRollNo) {
      throw new Error('This roll number is already registered. Please use a different roll number.');
    }
    
    // Step 1: Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Failed to create user');

    // Step 2: Upload profile picture if provided
    let profilePictureUrl = null;
    if (data.selfie) {
      const base64Data = data.selfie.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      const fileName = `${authData.user.id}/profile-${Date.now()}.jpg`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
      } else {
        const { data: { publicUrl } } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(fileName);
        profilePictureUrl = publicUrl;
      }
    }

    // Step 3: Create profile record with pending status
    // First check if profile already exists (in case of retry)
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    let profile;
    
    if (existingProfile) {
      // Profile already exists, just return it
      profile = existingProfile;
    } else {
      // Create new profile
      const { data: newProfile, error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          roll_no: data.rollNo,
          name: data.name,
          email: data.email,
          department: data.department,
          profile_picture: profilePictureUrl,
          points: 0,
          rank: 'beginner',
          streak: 0,
          login_streak: 1,
          total_activities: 0,
          before_after_count: 0,
          video_count: 0,
          last_login_date: new Date().toISOString(),
          status: 'pending', // New users need admin approval
        })
        .select()
        .single();

      if (profileError) {
        
        // Delete the auth user since profile creation failed
        // Note: This requires server-side implementation. For now, just sign out
        await supabase.auth.signOut();
        
        // Check for specific errors
        if (profileError.code === '23505') {
          if (profileError.message.includes('profiles_roll_no_key')) {
            throw new Error('This roll number is already registered. Please use a different roll number.');
          } else if (profileError.message.includes('profiles_email_key')) {
            throw new Error('This email is already registered. Please use a different email.');
          }
        }
        
        throw new Error('Failed to complete registration. Please try again.');
      }
      
      profile = newProfile;
    }

    return {
      user: authData.user,
      profile,
    };
  },
};